export const CONTRACT_ITEM_IMPORT_COLUMNS = [
  { key: "importCode", header: "Mã hạng mục", aliases: ["STT", "Mã cấu trúc"] },
  { key: "itemName", header: "Hạng mục công việc" },
  { key: "unit", header: "ĐVT" },
  { key: "contractQuantity", header: "Khối lượng" },
  { key: "unitPrice", header: "Đơn giá" },
  { key: "startDate", header: "Ngày bắt đầu", aliases: ["Từ ngày"] },
  { key: "endDate", header: "Ngày kết thúc", aliases: ["Đến ngày"] },
  { key: "status", header: "Trạng thái" },
  { key: "priority", header: "Độ ưu tiên" },
  { key: "difficultyLevel", header: "Khó khăn" },
];

export const UNIT_OPTIONS = [
  "%",
  "Bộ",
  "Cái",
  "Điểm",
  "Gói",
  "ha",
  "kg",
  "m",
  "m²",
  "Mảnh QĐ",
  "Ngày",
  "Phần",
  "Tấn",
  "Tháng",
];

export const STATUS_OPTIONS = [
  "Chưa bắt đầu",
  "Đang thực hiện",
  "Hoàn thành",
  "Tạm dừng",
  "Hủy bỏ",
];

export const PRIORITY_OPTIONS = [
  { value: 1, label: "Cao" },
  { value: 2, label: "Trung bình" },
  { value: 3, label: "Thấp" },
];

export const DIFFICULTY_OPTIONS = [1, 2, 3, 4, 5];

const DETAIL_FIELDS = [
  "unit",
  "difficultyLevel",
  "contractQuantity",
  "unitPrice",
  "startDate",
  "endDate",
  "status",
  "priority",
];

const HEADER_BY_KEY = Object.fromEntries(
  CONTRACT_ITEM_IMPORT_COLUMNS.map((column) => [column.key, column.header]),
);

// Chuẩn hóa giá trị text để kiểm tra dữ liệu trống.
const normalizeText = (value) =>
  value === null || value === undefined ? "" : value.toString().trim();

// Chuẩn hóa giá trị text để tra cứu không phân biệt hoa thường.
const normalizeLookup = (value) => normalizeText(value).toLowerCase();

// Ghi nhận lỗi kiểm tra dữ liệu import theo dòng và cột.
const addError = (errors, row, columnKey, value, message) => {
  errors.push({
    rowNumber: row?.rowNumber || "",
    importCode: row?.sourceImportCode || row?.importCode || "",
    itemCode: row?.sourceImportCode || row?.importCode || "",
    column: columnKey ? HEADER_BY_KEY[columnKey] || columnKey : "",
    value: value === null || value === undefined ? "" : value,
    message,
  });
};

// Chuẩn hóa Mã hạng mục để xác định cấu trúc cha con.
export const normalizeImportCode = (value) =>
  normalizeText(value).replace(/\s+/g, "");

// Xác định cấp hạng mục từ Mã hạng mục.
const getLevel = (importCode) => importCode.split(".").length;

// Lấy STT cha từ Mã hạng mục của dòng hiện tại.
const getParentImportCode = (importCode) => {
  const parts = importCode.split(".");
  return parts.length > 1 ? parts.slice(0, -1).join(".") : "";
};

// Kiểm tra hạng mục import có dữ liệu chi tiết hay không.
const hasDetailValue = (item) =>
  DETAIL_FIELDS.some((field) => {
    const value = item[field];
    return value !== null && value !== undefined && normalizeText(value) !== "";
  });

// Kiểm tra hạng mục hiện có có dữ liệu chi tiết khác mặc định hay không.
const hasExistingDetailValue = (item) =>
  Boolean(
    normalizeText(item?.unit) ||
      (item?.contractQuantity !== null && item?.contractQuantity !== undefined) ||
      (item?.unitPrice !== null && item?.unitPrice !== undefined) ||
      item?.startDate ||
      item?.endDate ||
      (item?.status && normalizeText(item.status) !== "Chưa bắt đầu") ||
      (item?.priority !== null && item?.priority !== undefined && Number(item.priority) !== 2) ||
      (item?.difficultyLevel !== null &&
        item?.difficultyLevel !== undefined &&
        Number(item.difficultyLevel) !== 1),
  );

// Chuyển giá trị Excel sang số để kiểm tra khối lượng và đơn giá.
const parseNumberValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : Number.NaN;

  const rawValue = value.toString().trim();
  let normalized = rawValue;
  if (rawValue.includes(".") && rawValue.includes(",")) {
    normalized = rawValue.replace(/\./g, "").replace(",", ".");
  } else if (rawValue.includes(",")) {
    normalized = rawValue.replace(",", ".");
  } else if (/^\d{1,3}(\.\d{3})+$/.test(rawValue)) {
    normalized = rawValue.replace(/\./g, "");
  }

  const numberValue = Number(normalized);
  return Number.isFinite(numberValue) ? numberValue : Number.NaN;
};

// Chuyển giá trị Excel sang ngày ISO để kiểm tra ngày bắt đầu/kết thúc.
const parseDateValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }

  if (typeof value === "number") {
    const excelEpoch = new Date(Date.UTC(1899, 11, 30));
    const date = new Date(excelEpoch.getTime() + value * 86400000);
    return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
  }

  const rawValue = value.toString().trim();
  const isoMatch = rawValue.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  const vnMatch = rawValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  const parts = isoMatch
    ? [isoMatch[1], isoMatch[2], isoMatch[3]]
    : vnMatch
      ? [vnMatch[3], vnMatch[2], vnMatch[1]]
      : null;
  if (!parts) return null;

  const [year, month, day] = parts.map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return date.toISOString().slice(0, 10);
};

// Làm phẳng cây hạng mục hiện có để tra cứu hạng mục cha được chọn.
const flattenExistingItems = (items = [], level = 0) =>
  items.flatMap((item) => {
    const normalizedItem = {
      ...item,
      level_: item.level_ ?? level,
    };
    return [
      normalizedItem,
      ...flattenExistingItems(item.children || [], level + 1),
    ];
  });

// Tạo map tra cứu các giá trị cố định hợp lệ.
const buildLookupMap = (options) =>
  new Map(options.map((option) => [normalizeLookup(option), option]));

const priorityMap = new Map(
  PRIORITY_OPTIONS.flatMap((option) => [
    [normalizeLookup(option.label), option.value],
    [normalizeLookup(option.value), option.value],
  ]),
);

// Sắp xếp dòng import theo đúng thứ tự STT phân cấp.
const compareImportCodes = (a, b) => {
  const partsA = a.importCode.split(".").map(Number);
  const partsB = b.importCode.split(".").map(Number);
  for (let index = 0; index < Math.max(partsA.length, partsB.length); index += 1) {
    const valueA = partsA[index] || 0;
    const valueB = partsB[index] || 0;
    if (valueA !== valueB) return valueA - valueB;
  }
  return 0;
};

// Kiểm tra dữ liệu import hạng mục hợp đồng và trả về danh sách hợp lệ.
export const validateContractItemsImport = ({
  rows = [],
  existingItems = [],
  targetParentItemId = null,
} = {}) => {
  const errors = [];
  const existingFlatItems = flattenExistingItems(existingItems);
  const targetParent = targetParentItemId
    ? existingFlatItems.find((item) => item.itemId === targetParentItemId)
    : null;
  const targetParentLevel = targetParent ? Number(targetParent.level_ || 0) + 1 : 0;
  const targetParentImportCode = targetParent
    ? normalizeImportCode(targetParent.itemCode)
    : "";
  const fileByImportCode = new Map();
  const normalizedRows = [];
  const unitMap = buildLookupMap(UNIT_OPTIONS);
  const statusMap = buildLookupMap(STATUS_OPTIONS);

  if (targetParent && hasExistingDetailValue(targetParent)) {
    addError(
      errors,
      { rowNumber: "", importCode: targetParent.itemCode },
      "",
      "",
      "Hạng mục cha được chọn đang có dữ liệu chi tiết, không thể import thêm hạng mục con",
    );
  }

  rows.forEach((sourceRow) => {
    const sourceImportCode = normalizeImportCode(sourceRow.importCode);
    const row = {
      ...sourceRow,
      sourceImportCode,
      importCode: sourceImportCode,
      itemName: normalizeText(sourceRow.itemName),
      unit: normalizeText(sourceRow.unit),
      status: normalizeText(sourceRow.status),
    };

    if (!row.importCode) {
      addError(errors, row, "importCode", sourceRow.importCode, "Mã hạng mục là bắt buộc");
      return;
    }

    if (!/^\d+(\.\d+)*$/.test(row.importCode)) {
      addError(errors, row, "importCode", sourceRow.importCode, "Mã hạng mục không đúng định dạng");
      return;
    }

    if (targetParent) {
      if (row.importCode === targetParentImportCode) {
        return;
      }

      const targetParentPrefix = `${targetParentImportCode}.`;
      if (!row.importCode.startsWith(targetParentPrefix)) {
        addError(
          errors,
          row,
          "importCode",
          sourceRow.importCode,
          `Mã hạng mục phải nằm dưới hạng mục cha đã chọn ${targetParentImportCode}`,
        );
        return;
      }

      row.importCode = row.importCode.slice(targetParentPrefix.length);
    }

    row.level = getLevel(row.importCode);
    if (row.level > 4) {
      addError(
        errors,
        row,
        "importCode",
        row.importCode,
        `Hạng mục ${row.importCode} vượt quá cấp hạng mục hỗ trợ`,
      );
    }

    if (targetParentLevel + row.level > 4) {
      addError(
        errors,
        row,
        "importCode",
        row.importCode,
        "Cấu trúc import vượt quá 4 cấp khi thêm dưới hạng mục cha đã chọn",
      );
    }

    if (fileByImportCode.has(row.importCode)) {
      addError(errors, row, "importCode", row.importCode, "Mã hạng mục bị trùng trong file import");
    } else {
      fileByImportCode.set(row.importCode, row);
    }

    const parentImportCode = getParentImportCode(row.importCode);
    row.parentImportCode = parentImportCode || null;

    const quantity = parseNumberValue(sourceRow.contractQuantity);
    if (Number.isNaN(quantity) || (quantity !== null && quantity < 0)) {
      addError(errors, row, "contractQuantity", sourceRow.contractQuantity, "Khối lượng phải là số hợp lệ và không âm");
    }

    const unitPrice = parseNumberValue(sourceRow.unitPrice);
    if (Number.isNaN(unitPrice) || (unitPrice !== null && unitPrice < 0)) {
      addError(errors, row, "unitPrice", sourceRow.unitPrice, "Đơn giá phải là số hợp lệ và không âm");
    }

    if (row.unit && !unitMap.has(normalizeLookup(row.unit))) {
      addError(errors, row, "unit", sourceRow.unit, `"${row.unit}" không nằm trong danh sách đơn vị tính hợp lệ`);
    }

    const difficultyRaw = normalizeText(sourceRow.difficultyLevel);
    const difficulty = difficultyRaw ? Number(difficultyRaw) : null;
    if (difficultyRaw && !DIFFICULTY_OPTIONS.includes(difficulty)) {
      addError(errors, row, "difficultyLevel", sourceRow.difficultyLevel, `"${difficultyRaw}" không nằm trong danh sách mức khó khăn hợp lệ`);
    }

    if (row.status && !statusMap.has(normalizeLookup(row.status))) {
      addError(errors, row, "status", sourceRow.status, `"${row.status}" không nằm trong danh sách trạng thái hợp lệ`);
    }

    const priorityRaw = normalizeText(sourceRow.priority);
    const priority = priorityRaw ? priorityMap.get(normalizeLookup(priorityRaw)) : null;
    if (priorityRaw && !priority) {
      addError(errors, row, "priority", sourceRow.priority, `"${priorityRaw}" không nằm trong danh sách độ ưu tiên hợp lệ`);
    }

    const startDate = parseDateValue(sourceRow.startDate);
    const endDate = parseDateValue(sourceRow.endDate);
    if (sourceRow.startDate && !startDate) {
      addError(errors, row, "startDate", sourceRow.startDate, "Ngày bắt đầu phải đúng định dạng ngày");
    }
    if (sourceRow.endDate && !endDate) {
      addError(errors, row, "endDate", sourceRow.endDate, "Ngày kết thúc phải đúng định dạng ngày");
    }
    if (startDate && endDate && endDate < startDate) {
      addError(errors, row, "endDate", sourceRow.endDate, "Ngày kết thúc không được nhỏ hơn ngày bắt đầu");
    }

    normalizedRows.push({
      ...row,
      unit: row.unit ? unitMap.get(normalizeLookup(row.unit)) || row.unit : "",
      status: row.status ? statusMap.get(normalizeLookup(row.status)) || row.status : "",
      difficultyLevel: difficulty,
      contractQuantity: Number.isNaN(quantity) ? null : quantity,
      unitPrice: Number.isNaN(unitPrice) ? null : unitPrice,
      startDate,
      endDate,
      priority,
    });
  });

  const fileChildCounts = new Map();
  normalizedRows.forEach((row) => {
    if (row.parentImportCode) {
      fileChildCounts.set(row.parentImportCode, (fileChildCounts.get(row.parentImportCode) || 0) + 1);
    }
  });

  normalizedRows.forEach((row) => {
    const hasChildren = (fileChildCounts.get(row.importCode) || 0) > 0;

    if (row.parentImportCode && !fileByImportCode.has(row.parentImportCode)) {
      addError(errors, row, "importCode", row.importCode, `Thiếu hạng mục cha ${row.parentImportCode}`);
    }

    if (hasChildren && hasDetailValue(row)) {
      addError(errors, row, "", "", "Hạng mục có con không được có dữ liệu chi tiết");
    }

    if (row.parentImportCode) {
      const parentInFile = fileByImportCode.get(row.parentImportCode);
      if (parentInFile && hasDetailValue(parentInFile)) {
        addError(errors, row, "", "", "Không được phép có cả cha và con cùng có dữ liệu chi tiết");
      }
    }
  });

  const validRows = normalizedRows
    .sort(compareImportCodes)
    .map((row) => ({
      importCode: row.importCode,
      itemName: row.itemName,
      unit: row.unit || null,
      contractQuantity: row.contractQuantity,
      unitPrice: row.unitPrice,
      parentImportCode: row.parentImportCode,
      startDate: row.startDate,
      endDate: row.endDate,
      status: row.status || null,
      priority: row.priority,
        completionPercentage: 0,
      difficultyLevel: row.difficultyLevel,
      notes: null,
    }));

  return {
    isValid: errors.length === 0 && validRows.length > 0,
    errors,
    items: errors.length === 0 ? validRows : [],
  };
};

export default validateContractItemsImport;
